import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { productValueForZoho, submitZohoWebform } from "@/lib/zohoWebform";
import { Loader2 } from "lucide-react";
import ZohoLeadFields from "@/components/ZohoLeadFields";

const EMPTY = {
  first_name: "",
  last_name: "",
  company_name: "",
  country: "",
  phone: "",
  email: "",
  product_interest: "",
  buyer_type: "",
  lead_status: "New",
  quantity: "",
  message: "",
};

export default function QuoteDialog({ children, product = "", open, onOpenChange }) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = open !== undefined;
  const dialogOpen = isControlled ? open : internalOpen;
  const setOpen = isControlled ? onOpenChange : setInternalOpen;

  const [form, setForm] = useState({ ...EMPTY, product_interest: productValueForZoho(product) });
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.last_name.trim() || !form.company_name.trim() || !form.buyer_type) {
      toast.error("Please provide your company, last name, and buyer type.");
      return;
    }
    setSubmitting(true);
    try {
      submitZohoWebform(form);
    } catch {
      toast.error("Submission failed. Please try again.");
      setSubmitting(false);
    }
  };

  const field =
    "bg-transparent border-0 border-b border-brand-line rounded-none px-0 py-3 focus-visible:ring-0 focus-visible:border-brand-gold transition-colors placeholder:text-brand-muted/60";

  return (
    <Dialog open={dialogOpen} onOpenChange={setOpen}>
      {children && <DialogTrigger asChild>{children}</DialogTrigger>}
      <DialogContent className="max-w-2xl bg-brand-cream border-brand-line max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl font-medium text-brand-green">
            Request a Quote
          </DialogTitle>
          <DialogDescription className="text-brand-muted">
            Share your requirement and our team will respond with pricing & availability.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5 pt-2" data-testid="quote-form">
          <ZohoLeadFields form={form} set={set} field={field} prefix="quote" />
          <button
            type="submit"
            data-testid="quote-submit"
            disabled={submitting}
            className="sm:col-span-2 mt-2 inline-flex items-center justify-center gap-2 bg-brand-green text-white px-8 py-4 rounded-full hover:bg-brand-green-dark transition-colors duration-300 font-medium tracking-wide disabled:opacity-60"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {submitting ? "Sending…" : "Submit Inquiry"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
